// ./src/components/features/Donations/MonthlyDonorsList/MonthlyDonorsList.tsx
//
// ROADMAP #204 step 6a — the account's monthly donors. A TABLE on desktop (donor · cause · each month · status · since ·
// next charge · open), CARDS below it (swipe on phones, a button on a tablet). The row's one action is "Open" — the
// drawer holds its months and Stop. Rows are never click-to-open (tap-safe). Shape of `DonationsList`.
import React, { useCallback, useMemo } from "react";
import { Paper, Stack, Group, Text, Box, Badge, ActionIcon, Tooltip, Skeleton, Table, VisuallyHidden } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { RecurringGift } from "@/utils/api";
import { useAppEnvironment } from "@/contexts";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  countCardActions,
  TableDateTime,
  type CardSkeletonShape,
} from "@/components/generic";
import { formatCurrency, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";

export interface MonthlyDonorsListProps {
  gifts: RecurringGift[];
  /** REQUIRED — the list takes its rows only as props. */
  isLoaded: boolean;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onOpen: (gift: RecurringGift) => void;
  highlightedId?: string | null;
  /** A search or a filter is narrowing the list (an empty result is "no match"). */
  hasQueryNarrowing: boolean;
  /** Cause names by id. */
  causeNames: Map<string, string>;
}

const STATUS_COLOR: Record<string, string> = { active: "green", past_due: "orange", canceled: "gray", incomplete: "blue", disconnected: "red" };

export const useMonthlyLabels = () => {
  const { t } = useTranslation(["donations"]);

  const statusLabel = useCallback(
    (s?: string) =>
      s === "active"
        ? t("donations:monthly.status.active", { defaultValue: "Active" })
        : s === "past_due"
          ? t("donations:monthly.status.pastDue", { defaultValue: "Card failing" })
          : s === "canceled"
            ? t("donations:monthly.status.canceled", { defaultValue: "Stopped" })
            : s === "disconnected"
              ? t("donations:monthly.status.disconnected", { defaultValue: "Stripe disconnected" })
              : t("donations:monthly.status.incomplete", { defaultValue: "Not started" }),
    [t]
  );

  return { statusLabel, statusColor: (s?: string) => STATUS_COLOR[s ?? ""] ?? "gray" };
};

const DonorCell: React.FC<{ gift: RecurringGift }> = ({ gift }) => {
  const { t } = useTranslation(["donations"]);

  return (
    <Box style={{ minWidth: 0 }}>
      <Group gap={6} wrap="wrap">
        <Text size="md" fw={500} lineClamp={1}>
          {gift.buyerName || gift.buyerEmail || "—"}
        </Text>
        {gift.isTest && (
          <Badge size="md" variant="light" color="yellow">
            {t("donations:list.test", { defaultValue: "Test" })}
          </Badge>
        )}
      </Group>
      {gift.buyerName && gift.buyerEmail && (
        <Text size="md" c="dimmed" lineClamp={1}>
          {gift.buyerEmail}
        </Text>
      )}
    </Box>
  );
};

const OpenButton: React.FC<{ gift: RecurringGift; onOpen: (g: RecurringGift) => void }> = ({ gift, onOpen }) => {
  const { t } = useTranslation(["donations"]);
  const label = t("donations:monthly.open", { defaultValue: "Open monthly gift" });

  return (
    <Tooltip label={label} withArrow position="bottom">
      <ActionIcon variant="subtle" color="blue" size="md" onClick={() => onOpen(gift)} aria-label={label}>
        <AppIcon iconName="ChevronRight" size={16} />
      </ActionIcon>
    </Tooltip>
  );
};

interface RowProps {
  gift: RecurringGift;
  onOpen: (g: RecurringGift) => void;
  highlighted: boolean;
  language: string;
  causeName: string;
  statusText: string;
  statusColor: string;
}

const MonthlyTableRow: React.FC<RowProps> = ({ gift, onOpen, highlighted, language, causeName, statusText, statusColor }) => (
  <Table.Tr style={{ backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : undefined, opacity: gift.recurringStatus === "canceled" ? 0.6 : 1 }}>
    <Table.Td>
      <DonorCell gift={gift} />
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <Text size="md" lineClamp={1}>
        {causeName}
      </Text>
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Text size="md" fw={500}>
        {formatCurrency(gift.amount ?? 0, gift.currencyCode, language)}
      </Text>
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Badge variant="light" color={statusColor} size="md">
        {statusText}
      </Badge>
    </Table.Td>
    <Table.Td style={{ width: 170 }}>
      <TableDateTime value={gift.startedAt ?? gift.createdAt} />
    </Table.Td>
    <Table.Td style={{ width: 170 }}>
      {gift.recurringStatus === "canceled" || gift.recurringStatus === "disconnected" || !gift.currentPeriodEnd ? <Text size="md">—</Text> : <TableDateTime value={gift.currentPeriodEnd} />}
    </Table.Td>
    <Table.Td style={{ width: 56 }}>
      <OpenButton gift={gift} onOpen={onOpen} />
    </Table.Td>
  </Table.Tr>
);

const MonthlyTableRowSkeleton: React.FC = () => (
  <Table.Tr>
    <Table.Td>
      <Skeleton height={18} width="55%" maw={240} />
      <Skeleton height={14} width="40%" maw={180} mt={4} />
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <Skeleton height={16} width={140} />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Skeleton height={18} width={80} />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Skeleton height={22} width={80} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 170 }}>
      <Skeleton height={16} width={110} />
    </Table.Td>
    <Table.Td style={{ width: 170 }}>
      <Skeleton height={16} width={110} />
    </Table.Td>
    <Table.Td style={{ width: 56 }}>
      <Skeleton circle height={28} width={28} />
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const MonthlyCardComponent: React.FC<CardProps> = (props) => {
  const { gift, onOpen, highlighted, language, causeName, statusText, statusColor, forceClose, onForceCloseComplete, onSwipeStateChange } = props;
  const { t } = useTranslation(["donations"]);

  // "Open" is the row's identity action — the right side (swipe the card left).
  const swipeActions = useMemo((): SwipeAction[] => [{ key: "open", icon: "ChevronRight", label: t("donations:list.openShort", { defaultValue: "Open" }), color: "blue", onClick: () => onOpen(gift) }], [gift, onOpen, t]);

  const cardBody = (showActions: boolean) => (
    <Paper p="md" withBorder radius="md" opacity={gift.recurringStatus === "canceled" ? 0.6 : 1} style={highlighted ? HIGHLIGHT_STYLE : undefined}>
      <Group gap="md" wrap="nowrap" align="flex-start">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <DonorCell gift={gift} />
            <Text size="md" fw={600}>
              {t("donations:monthly.perMonth", { defaultValue: "{{amount}} / month", amount: formatCurrency(gift.amount ?? 0, gift.currencyCode, language) })}
            </Text>
          </Group>
          <Text size="md" c="dimmed" lineClamp={1}>
            {causeName}
          </Text>
          <Group gap="xs" mt={4} wrap="wrap">
            <Badge variant="light" color={statusColor} size="md">
              {statusText}
            </Badge>
            <TableDateTime value={gift.startedAt ?? gift.createdAt} inline />
          </Group>
        </Box>
        {showActions && <OpenButton gift={gift} onOpen={onOpen} />}
      </Group>
    </Paper>
  );

  return (
    <>
      <Box hiddenFrom="sm">
        <SwipeableCard
          leftSwipeActions={swipeActions}
          forceClose={forceClose}
          onForceCloseComplete={onForceCloseComplete}
          onSwipeStateChange={onSwipeStateChange}
          borderRadius="var(--mantine-radius-md)"
        >
          {cardBody(false)}
        </SwipeableCard>
      </Box>
      <Box visibleFrom="sm">{cardBody(true)}</Box>
    </>
  );
};

const MonthlyCard = React.memo(MonthlyCardComponent);

const MonthlyCardSkeleton: React.FC<CardSkeletonShape> = ({ actions }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <Box style={{ flex: 1, minWidth: 0 }}>
        <CardSkeletonLines count={3} sizes={["md", "md", "md"]} gap={4} />
      </Box>
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

const MonthlyDonorsListComponent: React.FC<MonthlyDonorsListProps> = ({ gifts, isLoaded, loading, loadingMore, hasMore, onLoadMore, onOpen, highlightedId, hasQueryNarrowing, causeNames }) => {
  const { t, i18n } = useTranslation(["donations", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;
  const { statusLabel, statusColor } = useMonthlyLabels();
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleOpen = useCallback(
    (g: RecurringGift) => {
      closeAllCards();
      onOpen(g);
    },
    [onOpen, closeAllCards]
  );
  // The tablet card's row buttons — exactly the handlers its rows get (▶ check-card-skeletons).
  const cardActions = countCardActions(handleOpen);

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY (▶ check-list-states).
  const showSkeleton = !isLoaded;

  if (!showSkeleton && gifts.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("donations:monthly.noMatch", { defaultValue: "No monthly donors match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("donations:monthly.empty.title", { defaultValue: "No monthly donors yet" })}
          description={t("donations:monthly.empty.description", {
            defaultValue: "When someone chooses Monthly on your gift form, they appear here. Every month they give is also in Gifts.",
          })}
          iconName="Heart"
          size="md"
        />
      </Paper>
    );
  }

  const rowProps = (g: RecurringGift): RowProps => ({
    gift: g,
    onOpen: handleOpen,
    highlighted: g.id === highlightedId,
    language,
    causeName: (g.causeId && causeNames.get(g.causeId)) || "—",
    statusText: statusLabel(g.recurringStatus),
    statusColor: statusColor(g.recurringStatus),
  });

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("donations:list.columns.donor", { defaultValue: "Donor" })}</Table.Th>
                <Table.Th style={{ width: 200 }}>{t("donations:list.columns.cause", { defaultValue: "Cause" })}</Table.Th>
                <Table.Th style={{ width: 140 }}>{t("donations:monthly.columns.amount", { defaultValue: "Each month" })}</Table.Th>
                <Table.Th style={{ width: 140 }}>{t("donations:list.columns.status", { defaultValue: "Status" })}</Table.Th>
                <Table.Th style={{ width: 170 }}>{t("donations:monthly.columns.since", { defaultValue: "Since" })}</Table.Th>
                <Table.Th style={{ width: 170 }}>{t("donations:monthly.columns.next", { defaultValue: "Next charge" })}</Table.Th>
                <Table.Th style={{ width: 56 }}>
                  <VisuallyHidden>{t("common:labels.actions", { defaultValue: "Actions" })}</VisuallyHidden>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton ? Array.from({ length: 6 }).map((_, i) => <MonthlyTableRowSkeleton key={i} />) : gifts.map((g) => <MonthlyTableRow key={g.id} {...rowProps(g)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 5 }).map((_, i) => (
          <MonthlyCardSkeleton key={i} actions={cardActions} withCheckbox={false} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      {gifts.map((g) => (
        <MonthlyCard
          key={g.id}
          {...rowProps(g)}
          forceClose={shouldForceClose(g.id || "")}
          onForceCloseComplete={() => handleForceCloseComplete(g.id || "")}
          onSwipeStateChange={(isOpen) => {
            if (isOpen) {
              openCard(g.id || "");
            }
          }}
        />
      ))}
      {loadMore}
    </Stack>
  );
};

export const MonthlyDonorsList = React.memo(MonthlyDonorsListComponent);
